/**
 * @since 2026-04-18
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import {
  useAuthenticationService,
  LoginComponent,
  getAuthConfig,
  createMsalConfig,
  getInitialProvider,
  needsAuthentication,
} from "@algotraders/web-auth";
import "firebaseConfig";

import MenuBar from "components/MenuBar";
import PanelDashboard from "components/PanelDashboard";
import PanelSecurityDetail from "components/PanelSecurityDetail";
import { useNotification } from "components/Shared/Notifications";
import TextCopyright from "components/TextCopyright";
import { useActiveFixJobs } from "hooks/useActiveFixJobs";
import { usePendingOverrides } from "hooks/usePendingOverrides";
import { useUserRole } from "hooks/useUserRole";
import { getConfig } from "../../getConfig";
import { AuthenticationError } from "services/api";
import "../../App.scss";

const config = getConfig();
const { BACKEND_URL } = config;

// Display-only relabel of the daily bar (raw value "1Day" is kept for routing/keys).
const formatBarSize = (barSize) => (barSize === "1Day" ? "1Day_EndOfDay" : barSize || "");

const getPathSecurityAndBarSize = () => {
  const raw = (window.location.pathname || "").replace(/^\/+|\/+$/g, "");
  if (!raw) return { securityName: null, barSize: null };
  const parts = raw.split("/");
  const securityName = parts[0] ? decodeURIComponent(parts[0]) : null;
  const barSize = parts[1] ? decodeURIComponent(parts[1]) : null;
  return { securityName, barSize };
};

const getHashCheckName = () => {
  const raw = (window.location.hash || "").replace(/^#/, "");
  return raw ? decodeURIComponent(raw) : null;
};

const App = () => {
  const { notifyError, notifySuccess } = useNotification();

  const authConfig = useMemo(() => getAuthConfig(config), []);
  const firebaseAuth = useMemo(() => {
    try { return getAuth(); } catch { return null; }
  }, []);
  const azureMsalConfig = useMemo(() => createMsalConfig(authConfig), [authConfig]);
  const [selectedProvider, setSelectedProvider] = useState(() => getInitialProvider(authConfig));

  const {
    user,
    idToken,
    isLoading,
    isAuthenticated,
    signOut,
    loginPopup,
    getToken,
  } = useAuthenticationService({
    provider: selectedProvider,
    firebaseAuth: authConfig.firebase.enabled ? firebaseAuth : null,
    msalConfig: authConfig.azure.enabled ? azureMsalConfig : null,
    backendUrl: `${BACKEND_URL}/api`,
    tokenRefreshInterval: 50 * 60 * 1000,
    onError: (e) => notifyError(e?.message || "Authentication error"),
  });

  useEffect(() => {
    if (selectedProvider) {
      try { localStorage.setItem("authProvider", selectedProvider); } catch {}
    }
  }, [selectedProvider]);

  const handleLogout = useCallback(async (message) => {
    if (message) notifyError(message);
    try {
      if (idToken) {
        try {
          await fetch(`${BACKEND_URL}/api/logout/`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
          });
        } catch {}
      }
      await signOut();
      try { localStorage.removeItem("authProvider"); } catch {}
      if (!message) notifySuccess("Logged out");
    } catch {
      notifyError("Error during logout");
    }
  }, [idToken, signOut, notifyError, notifySuccess]);

  const handleFirebaseLogin = useCallback(async () => {
    setSelectedProvider("firebase");
    try { localStorage.setItem("authProvider", "firebase"); } catch {}

    // Wait for the provider switch to settle, then open the Google popup.
    setTimeout(async () => {
      try {
        if (firebaseAuth && authConfig.firebase.enabled) {
          const { signInWithPopup, GoogleAuthProvider } = await import("firebase/auth");
          const provider = new GoogleAuthProvider();
          await signInWithPopup(firebaseAuth, provider);
        }
      } catch (e) {
        console.error("Firebase login error:", e);
        notifyError(e?.message || "Firebase login failed");
      }
    }, 500);
  }, [firebaseAuth, authConfig, notifyError]);

  const handleAzureLogin = useCallback(() => {
    if (selectedProvider !== "azure") {
      setSelectedProvider("azure");
      try { localStorage.setItem("authProvider", "azure"); } catch {}
      setTimeout(() => loginPopup?.(), 500);
    } else {
      loginPopup?.();
    }
  }, [selectedProvider, loginPopup]);

  const { userRole } = useUserRole(idToken);
  const activeFixJobs = useActiveFixJobs({ idToken, getToken, enabled: !!idToken });
  const pendingOverrides = usePendingOverrides({ idToken, getToken, enabled: !!idToken });

  useEffect(() => {
    if (activeFixJobs.completionTick === 0) return;
    pendingOverrides.refetch();
  }, [activeFixJobs.completionTick, pendingOverrides]);

  const [selectedSecurity, setSelectedSecurity] = useState(null);
  const initialPath = useMemo(() => getPathSecurityAndBarSize(), []);
  const [pendingSecurityName, setPendingSecurityName] = useState(initialPath.securityName);
  const [pendingBarSize, setPendingBarSize] = useState(initialPath.barSize);
  const [pendingCheckName, setPendingCheckName] = useState(() => getHashCheckName());

  useEffect(() => {
    const handler = () => {
      const { securityName, barSize } = getPathSecurityAndBarSize();
      const checkName = getHashCheckName();
      setSelectedSecurity(null);
      setPendingSecurityName(securityName);
      setPendingBarSize(barSize);
      setPendingCheckName(checkName);
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  const handleSelectSecurity = useCallback((sec) => {
    setPendingSecurityName(null);
    setPendingBarSize(null);
    const checkName = sec?.initialCheckName || null;
    setPendingCheckName(checkName);
    setSelectedSecurity(sec);
    let pathPart = "/";
    if (sec) {
      pathPart = `/${encodeURIComponent(sec.securityName)}`;
      if (sec.barSize) pathPart += `/${encodeURIComponent(sec.barSize)}`;
    }
    const hashPart = checkName ? `#${encodeURIComponent(checkName)}` : "";
    const searchPart = window.location.search || "";
    const target = `${pathPart}${searchPart}${hashPart}`;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (current !== target) {
      window.history.pushState({}, "", target);
    }
  }, []);

  const handleBackToDashboard = useCallback(() => {
    setSelectedSecurity(null);
    setPendingSecurityName(null);
    setPendingBarSize(null);
    setPendingCheckName(null);
    const searchPart = window.location.search || "";
    const target = `/${searchPart}`;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (current !== target) {
      window.history.pushState({}, "", target);
    }
  }, []);

  useEffect(() => {
    const secName = selectedSecurity?.securityName || pendingSecurityName;
    const barSize = selectedSecurity?.barSize;
    const suffix = barSize ? ` | ${formatBarSize(barSize)}` : "";
    document.title = secName ? `Review | ${secName}${suffix}` : "Dataset Review";
  }, [selectedSecurity, pendingSecurityName]);

  const authRequired = needsAuthentication(authConfig);

  const handleApiError = useCallback((e) => {
    if (e instanceof AuthenticationError) handleLogout(e.message);
    else notifyError(e?.message || "Request failed");
  }, [handleLogout, notifyError]);

  if (authRequired && isLoading) {
    return (
      <div className="app-container">
        <div className="content">
          <div className="authenticating">Authenticating...</div>
        </div>
        <TextCopyright />
      </div>
    );
  }

  if (authRequired && !isAuthenticated) {
    return (
      <div className="app-container">
        <div className="content">
          <div className="login-screen">
            <LoginComponent
              title={getConfig().DISPLAY_NAME || "Dataset Review"}
              logoUrl={authConfig.imageUrl}
              isFirebaseEnabled={authConfig.firebase.enabled}
              isAzureEnabled={authConfig.azure.enabled}
              onLoginSuccess={async () => {}}
              onFirebaseLogin={handleFirebaseLogin}
              onAzureLogin={handleAzureLogin}
            />
          </div>
        </div>
        <TextCopyright />
      </div>
    );
  }

  return (
    <div className="app-container">
      <MenuBar
        user={user}
        onLogout={() => handleLogout()}
        onNavigateHome={handleBackToDashboard}
        idToken={idToken}
        getToken={getToken}
        activeJobs={activeFixJobs.activeJobs}
        pendingOverrides={pendingOverrides.overrides}
        pendingOverrideCount={pendingOverrides.count}
        refetchPendingOverrides={pendingOverrides.refetch}
        canRunFixJob={["PM", "OPERATOR"].includes((userRole || "").toUpperCase())}
      />
      <div className="content">
        {selectedSecurity ? (
          <PanelSecurityDetail
            securityType={selectedSecurity.securityType}
            securityName={selectedSecurity.securityName}
            barSize={selectedSecurity.barSize}
            idToken={idToken}
            getToken={getToken}
            userRole={userRole}
            initialCheckName={pendingCheckName}
            onBack={handleBackToDashboard}
            onApiError={handleApiError}
            fixJobCompletionTick={activeFixJobs.completionTick}
          />
        ) : (
          <PanelDashboard
            idToken={idToken}
            getToken={getToken}
            userRole={userRole}
            initialCheckName={pendingCheckName}
            onSelectSecurity={handleSelectSecurity}
            onApiError={handleApiError}
            initialSecurityName={pendingSecurityName}
            initialBarSize={pendingBarSize}
            activeFixJobs={activeFixJobs}
          />
        )}
      </div>
      <TextCopyright />
    </div>
  );
};

export default App;
